import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "pl", label: "lang_pl" },
  { code: "en", label: "lang_en" },
  { code: "es", label: "lang_es" },
];

export default function LanguageSwitcher({
  compact = false,
  dark = false,
}: {
  compact?: boolean;
  dark?: boolean;
}) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = languages.find((l) => l.code === i18n.language) || languages[0];

  const baseBtn =
    "flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap cursor-pointer";
  const btnClass = dark
    ? `${baseBtn} text-background-50 hover:bg-background-50/15`
    : `${baseBtn} text-foreground-700 hover:bg-background-100`;

  if (compact) {
    return (
      <select
        value={i18n.language}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        className="text-xs bg-transparent border border-background-200 rounded-md px-2 py-1 text-foreground-700 focus:outline-none cursor-pointer"
      >
        {languages.map((l) => (
          <option key={l.code} value={l.code}>
            {l.code.toUpperCase()}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((s) => !s)} className={btnClass}>
        <i className="ri-global-line"></i>
        <span>{t(current.label)}</span>
        <i className={`ri-arrow-down-s-line text-[10px] ${open ? "rotate-180" : ""} transition-transform`}></i>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg overflow-hidden min-w-[140px] z-50">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                i18n.changeLanguage(l.code);
                setOpen(false);
              }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                current.code === l.code
                  ? "bg-accent-50 text-accent-700 font-medium"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {t(l.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}